import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
} from '@mui/material';
import { useUnit } from 'effector-react';
import { styled } from '@mui/material/styles';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { grey } from '@mui/material/colors';
import {
  $cardsList,
  $searchBoards,
  $searchBoardsLoading,
  $searchCatalogError,
  searchFiltersOpened,
  $searchLoading,
  $searchSettings,
  DEFAULT_SEARCH_SETTINGS,
  SearchSettings,
  getSearchSettingsError,
  searchSettingsApplied,
  onLoadCards,
  onResetCardsList,
  searchCards,
} from './model';
import {
  $workspaceList,
  workspaceGetListFX,
} from '../../../../GRID/model/workspace';
import { Board } from '../../../../apiRPC/board';
import { themeGrid } from '../../../../theme/themeGrid';
import { makeSlug } from '../../../../helper/makeSlug';
import { navigateForEffect } from '../../../../model/routing';

type SearchOption = { uuid: string; title: string; unavailable?: boolean };

function withSelectedOptions(
  options: SearchOption[],
  selected: string[],
): SearchOption[] {
  return [
    ...options,
    ...selected
      .filter((uuid) => !options.some((option) => option.uuid === uuid))
      .map((uuid) => ({
        uuid,
        title: `Недоступно · ${uuid}`,
        unavailable: true,
      })),
  ];
}

const FormSelect = styled(FormControl)({
  minWidth: 220,
  flexGrow: 1,
  marginRight: 10,
  '& .MuiFormLabel-root': {
    color: 'white',
    '&.Mui-focused': { color: grey[200] },
  },
  '& .MuiInputBase-root': {
    color: 'white',
    '&:before': { borderColor: 'white !important' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: grey[200],
    },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
  '& .MuiSvgIcon-root': { color: 'white' },
});
const theme = createTheme(themeGrid);
const searchAreas: { key: keyof SearchSettings['searchIn']; label: string }[] =
  [
    { key: 'title', label: 'Название' },
    { key: 'description', label: 'Описание' },
    { key: 'comments', label: 'Комментарии' },
    { key: 'uuid', label: 'UUID' },
  ];
const dateFields: {
  key: 'startDate' | 'endDate';
  label: string;
  clearLabel: string;
}[] = [
  { key: 'startDate', label: 'От', clearLabel: 'Очистить дату начала' },
  { key: 'endDate', label: 'До', clearLabel: 'Очистить дату окончания' },
];
const hasFilters = (settings: SearchSettings) =>
  Boolean(
    settings.workspaces.length ||
      settings.boards.length ||
      settings.startDate ||
      settings.endDate ||
      Object.values(settings.searchIn).some((value) => !value),
  );
const PaperComponent: FC<React.HTMLAttributes<HTMLElement>> = (props) => (
  <ThemeProvider theme={theme}>
    <Paper {...props} />
  </ThemeProvider>
);
const SearchListbox = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLElement>
>(({ children, ...props }, ref) => {
  const loading = useUnit($searchLoading);
  return (
    <ul {...props} ref={ref}>
      {children}
      {loading && (
        <Box
          component="li"
          role="presentation"
          sx={{
            px: 2,
            py: 1,
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Typography role="status" variant="body2" color="text.secondary">
            Загрузка…
          </Typography>
        </Box>
      )}
    </ul>
  );
});

export const GlobalSearch: FC = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollTopRef = useRef(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const settingsOpen = Boolean(anchorEl);
  const {
    cardsList,
    settings,
    loading,
    workspaceList,
    workspacesLoading,
    boards,
    boardsLoading,
    catalogError,
  } = useUnit({
    cardsList: $cardsList,
    settings: $searchSettings,
    loading: $searchLoading,
    workspaceList: $workspaceList,
    workspacesLoading: workspaceGetListFX.pending,
    boards: $searchBoards,
    boardsLoading: $searchBoardsLoading,
    catalogError: $searchCatalogError,
  });
  const [draft, setDraft] = useState(settings);
  useEffect(() => {
    if (!cardsList?.length) {
      scrollTopRef.current = 0;
      listboxRef.current = null;
      return;
    }
    // MUI resets scroll when options are appended without a highlighted item.
    if (listboxRef.current) listboxRef.current.scrollTop = scrollTopRef.current;
  }, [cardsList]);
  const workspaceOptions = withSelectedOptions(
    workspaceList?.rows || [],
    draft.workspaces,
  );
  const getBoardLabel = (board: Board) =>
    `${board.title} · ${
      workspaceOptions.find(({ uuid }) => uuid === board.workspace)?.title ||
      board.workspace
    }`;
  const boardOptions = withSelectedOptions(
    (boards || [])
      .filter(
        (board) =>
          !draft.workspaces.length ||
          draft.workspaces.includes(board.workspace),
      )
      .map((board) => ({ uuid: board.uuid, title: getBoardLabel(board) })),
    draft.boards,
  );
  const catalogLoading = workspacesLoading || boardsLoading;
  const settingsError = getSearchSettingsError(draft);
  const activeFilters = hasFilters(settings);
  const selectedAreas = searchAreas
    .filter(({ key }) => settings.searchIn[key])
    .map(({ label }) => label)
    .join(', ');
  const filterSummary = `${selectedAreas}${
    settings.startDate ? ` · от ${settings.startDate}` : ''
  }${settings.endDate ? ` · до ${settings.endDate}` : ''}${
    settings.workspaces.length
      ? ` · Пространств: ${settings.workspaces.length}`
      : ''
  }${settings.boards.length ? ` · Досок: ${settings.boards.length}` : ''}`;

  const closeSettings = () => {
    setAnchorEl(null);
    buttonRef.current?.focus();
  };

  return (
    <FormSelect
      sx={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      <Autocomplete
        options={cardsList || []}
        inputValue={text}
        value={null}
        forcePopupIcon={false}
        slotProps={{
          popper: {
            placement: 'bottom-end',
            style: { width: 500, maxWidth: 'calc(100vw - 16px)' },
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  padding: 8,
                  rootBoundary: 'viewport',
                  tether: false,
                },
              },
            ],
          },
        }}
        open={open && !settingsOpen && text.trim().length >= 3}
        openOnFocus
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onBlur={() => setOpen(false)}
        sx={{ width: open ? '500px' : '150px', maxWidth: '100%', ml: 'auto' }}
        loading={loading}
        loadingText="Поиск…"
        noOptionsText="Ничего не найдено"
        clearText="Очистить поиск"
        ListboxComponent={SearchListbox}
        ListboxProps={{
          onScroll: ({ currentTarget }) => {
            listboxRef.current = currentTarget;
            scrollTopRef.current = currentTarget.scrollTop;
            const scrollPosition =
              currentTarget.scrollTop + currentTarget.clientHeight;
            if (currentTarget.scrollHeight - scrollPosition <= 1) onLoadCards();
          },
        }}
        getOptionLabel={(option) =>
          `${option.boardTitle} • ${option.columnTitle} • ${option.title}`
        }
        PaperComponent={PaperComponent}
        onChange={(_, option) => {
          if (option)
            navigateForEffect(`/c/${option.uuid}/${makeSlug(option.title)}`);
        }}
        renderOption={(props, option) => (
          <Box {...props} component="li" key={option.uuid}>
            <Box
              component="a"
              href={`/c/${option.uuid}/${makeSlug(option.title)}`}
              onClick={(event) => {
                if (
                  event.ctrlKey ||
                  event.metaKey ||
                  event.shiftKey ||
                  event.altKey
                ) {
                  event.stopPropagation();
                  return;
                }
                event.preventDefault();
              }}
              tabIndex={-1}
              sx={{
                width: '100%',
                color: 'inherit',
                textDecoration: 'none',
                overflowWrap: 'anywhere',
              }}
            >
              {option.title}
              <Typography
                variant="body2"
                color="text.secondary"
                fontSize="small"
              >
                {option.boardTitle} • {option.columnTitle}
              </Typography>
            </Box>
          </Box>
        )}
        filterOptions={(options) => options}
        clearOnBlur={false}
        onInputChange={(_, value, reason) => {
          if (reason !== 'input' && reason !== 'clear') return;
          setText(value);
          if (reason === 'clear') onResetCardsList();
          else searchCards(value);
          setOpen(true);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            inputProps={{ ...params.inputProps, maxLength: 80 }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={18} />}
                  {text && (
                    <IconButton
                      size="small"
                      aria-label="Очистить поиск"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setText('');
                        onResetCardsList();
                        setOpen(false);
                        inputRef.current?.focus();
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            label="Поиск"
            placeholder="Поиск"
            size="small"
            onClick={() => setOpen(true)}
          />
        )}
      />
      <Tooltip
        title={
          settingsOpen
            ? ''
            : (activeFilters && filterSummary) ||
              'Все области · Без ограничения дат'
        }
      >
        <IconButton
          ref={buttonRef}
          aria-label={
            activeFilters ? 'Фильтры поиска (активны)' : 'Фильтры поиска'
          }
          aria-expanded={settingsOpen}
          aria-controls={settingsOpen ? 'search-filters-panel' : undefined}
          aria-haspopup="dialog"
          sx={{
            ml: 0.5,
            backgroundColor: activeFilters
              ? 'rgba(255,255,255,0.18)'
              : undefined,
          }}
          onClick={(event) => {
            if (settingsOpen) closeSettings();
            else {
              setDraft(settings);
              searchFiltersOpened();
              setAnchorEl(event.currentTarget);
            }
          }}
        >
          <Badge variant="dot" color="warning" invisible={!activeFilters}>
            <FilterAltOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <ThemeProvider theme={theme}>
        <Popper
          open={settingsOpen}
          anchorEl={anchorEl}
          placement="bottom-end"
          modifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
            {
              name: 'preventOverflow',
              options: { padding: 8, rootBoundary: 'viewport', tether: false },
            },
          ]}
          sx={{ zIndex: theme.zIndex.modal }}
        >
          <ClickAwayListener
            onClickAway={(event) => {
              if (
                event.target instanceof Node &&
                buttonRef.current?.contains(event.target)
              )
                return;
              setAnchorEl(null);
            }}
          >
            <Paper
              id="search-filters-panel"
              role="dialog"
              aria-modal="false"
              aria-labelledby="search-filters-title"
              elevation={8}
              sx={{
                width: 340,
                maxWidth: 'calc(100vw - 16px)',
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                p: 2,
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Escape' || event.isPropagationStopped())
                  return;
                event.stopPropagation();
                closeSettings();
              }}
              onBlur={(event) => {
                if (event.relatedTarget === buttonRef.current) return;
                if (
                  event.relatedTarget &&
                  !event.currentTarget.contains(event.relatedTarget)
                )
                  setAnchorEl(null);
              }}
            >
              <Typography
                id="search-filters-title"
                variant="subtitle1"
                fontWeight={600}
              >
                Фильтры поиска
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Искать в
              </Typography>
              <FormGroup>
                {searchAreas.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    label={label}
                    control={
                      <Checkbox
                        size="small"
                        autoFocus={key === 'title'}
                        checked={draft.searchIn[key]}
                        onChange={(_, checked) =>
                          setDraft({
                            ...draft,
                            searchIn: { ...draft.searchIn, [key]: checked },
                          })
                        }
                      />
                    }
                  />
                ))}
              </FormGroup>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  my: 1.5,
                  '& .MuiAutocomplete-endAdornment': { top: 6 },
                }}
              >
                <Autocomplete
                  multiple
                  disablePortal
                  disableCloseOnSelect
                  size="small"
                  options={workspaceOptions}
                  clearText="Очистить выбор рабочих пространств"
                  openText="Открыть список рабочих пространств"
                  closeText="Закрыть список рабочих пространств"
                  getOptionDisabled={(option) => Boolean(option.unavailable)}
                  value={workspaceOptions.filter(({ uuid }) =>
                    draft.workspaces.includes(uuid),
                  )}
                  loading={workspacesLoading}
                  disabled={workspacesLoading || !workspaceList}
                  loadingText="Загрузка…"
                  noOptionsText="Рабочие пространства не найдены"
                  getOptionLabel={({ title }) => title}
                  renderOption={(props, option) => (
                    <li {...props} key={option.uuid}>
                      {option.title}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.uuid === value.uuid
                  }
                  onChange={(_, selected) => {
                    const workspaces = selected.map(({ uuid }) => uuid);
                    const removed = draft.workspaces.filter(
                      (uuid) => !workspaces.includes(uuid),
                    );
                    setDraft({
                      ...draft,
                      workspaces,
                      boards: draft.boards.filter((uuid) => {
                        const board = boards?.find(
                          (item) => item.uuid === uuid,
                        );
                        if (!board) return true;
                        return (
                          !removed.includes(board.workspace) &&
                          (!workspaces.length ||
                            workspaces.includes(board.workspace))
                        );
                      }),
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Рабочие пространства" />
                  )}
                />
                <Autocomplete
                  multiple
                  disablePortal
                  disableCloseOnSelect
                  size="small"
                  options={boardOptions}
                  clearText="Очистить выбор досок"
                  openText="Открыть список досок"
                  closeText="Закрыть список досок"
                  getOptionDisabled={(option) => Boolean(option.unavailable)}
                  value={boardOptions.filter(({ uuid }) =>
                    draft.boards.includes(uuid),
                  )}
                  loading={catalogLoading}
                  disabled={catalogLoading || !boards || catalogError}
                  loadingText="Загрузка…"
                  noOptionsText="Доски не найдены"
                  getOptionLabel={({ title }) => title}
                  renderOption={(props, option) => (
                    <li {...props} key={option.uuid}>
                      {option.title}
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.uuid === value.uuid
                  }
                  onChange={(_, selected) =>
                    setDraft({
                      ...draft,
                      boards: selected.map(({ uuid }) => uuid),
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Доски"
                      helperText={
                        catalogLoading ? 'Загрузка досок…' : undefined
                      }
                    />
                  )}
                />
              </Box>
              {catalogError && (
                <Box>
                  <FormHelperText error role="alert">
                    Не удалось загрузить списки для фильтров
                  </FormHelperText>
                  <Button size="small" onClick={() => searchFiltersOpened()}>
                    Повторить загрузку
                  </Button>
                </Box>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, mb: 1.5 }}
              >
                Дата создания карточки
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {dateFields.map(({ key, label, clearLabel }) => (
                  <TextField
                    key={key}
                    label={label}
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={draft[key]}
                    onChange={(event) =>
                      setDraft({ ...draft, [key]: event.target.value })
                    }
                    InputProps={{
                      endAdornment: draft[key] ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label={clearLabel}
                            onClick={() => setDraft({ ...draft, [key]: '' })}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />
                ))}
              </Box>
              {settingsError && (
                <FormHelperText error role="alert">
                  {settingsError}
                </FormHelperText>
              )}
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 2 }}
              >
                <Button
                  size="small"
                  disabled={!hasFilters(draft)}
                  onClick={() => setDraft(DEFAULT_SEARCH_SETTINGS)}
                >
                  Сбросить
                </Button>
                <Button
                  size="small"
                  sx={{ ml: 'auto' }}
                  onClick={closeSettings}
                >
                  Отмена
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={Boolean(settingsError)}
                  onClick={() => {
                    searchSettingsApplied(draft);
                    setAnchorEl(null);
                    setOpen(true);
                    inputRef.current?.focus();
                  }}
                >
                  Применить
                </Button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </ThemeProvider>
    </FormSelect>
  );
};
